import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { PageHeader, ScopeNotice, useScopeGuard, fmtNum } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Play, Lock, ClipboardCheck, GitFork, CheckCircle2, AlertTriangle, ArrowLeft, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import QuickMaestroSelect from "@/components/QuickMaestroSelect";
import { setAriEstado } from "@/lib/ariEstado";

const ORDEN_META = {
  planificada: { label: "Planificada", cls: "bg-[#94A3B8] hover:bg-[#94A3B8]" },
  en_curso: { label: "En curso", cls: "bg-[#3B82F6] hover:bg-[#3B82F6]" },
  cerrada: { label: "Cerrada", cls: "bg-[#0E2A47] hover:bg-[#0E2A47]" },
};

export default function Produccion() {
  const { user, scopeTenant } = useAuth();
  const canEdit = ["superadmin", "admin", "calidad", "operario"].includes(user.rol);
  const needsScope = useScopeGuard(user, scopeTenant);

  const [ordenes, setOrdenes] = useState([]);
  const [sel, setSel] = useState(null); // orden detail with partes
  const [lotes, setLotes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [plantillas, setPlantillas] = useState([]);

  const [ordOpen, setOrdOpen] = useState(false);
  const [ord, setOrd] = useState({ producto_id: "", cantidad_planificada: "", unidad: "kg" });

  const [previa, setPrevia] = useState(null); // { parteId, data }
  const [regOpen, setRegOpen] = useState(false);
  const [reg, setReg] = useState({ parteId: null, plantilla_id: "", valores: {}, zona: "" });

  const loadBase = useCallback(async () => {
    if (needsScope) return;
    const [o, l, p, pl] = await Promise.all([
      api.get("/ordenes"), api.get("/lotes"), api.get("/productos"), api.get("/calidad/plantillas"),
    ]);
    setOrdenes(o.data); setLotes(l.data); setProductos(p.data); setPlantillas(pl.data);
  }, [needsScope]);
  useEffect(() => { loadBase(); }, [loadBase]);

  const openOrden = async (id) => { const { data } = await api.get(`/ordenes/${id}`); setSel(data); };
  const refreshSel = async () => { if (sel) await openOrden(sel.id); await loadBase(); };

  const crearOrden = async () => {
    if (!ord.producto_id) return toast.error("Seleccione el producto");
    if (!ord.cantidad_planificada || Number(ord.cantidad_planificada) <= 0) return toast.error("Cantidad no válida");
    try {
      await api.post("/ordenes", { producto_id: ord.producto_id, cantidad_planificada: Number(ord.cantidad_planificada), unidad: ord.unidad });
      toast.success("Orden creada"); setOrdOpen(false); setOrd({ producto_id: "", cantidad_planificada: "", unidad: "kg" }); loadBase();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const transicion = async (estado) => {
    try { await api.post(`/ordenes/${sel.id}/estado`, { estado }); toast.success(`Orden ${estado}`); refreshSel(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const crearParte = async () => {
    try { await api.post(`/partes?orden_id=${sel.id}`); toast.success("Parte creado"); refreshSel(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const verPrevia = async (parteId) => {
    try {
      const { data } = await api.get(`/partes/${parteId}/previa`);
      setPrevia({ parteId, data });
      const campos_vacios = [];
      if (!(data.total_pt > 0) && (data.consumo_total > 0)) campos_vacios.push("producto terminado (cantidad)");
      if ((data.mermas || []).some((m) => !m.motivo)) campos_vacios.push("motivo de merma");
      setAriEstado({ errores_visibles: data.errores || [], campos_vacios });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const cerrarParte = async (parteId) => {
    try { await api.post(`/partes/${parteId}/cerrar`); toast.success("Parte cerrado · lote PT creado"); setPrevia(null); refreshSel(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const crearAjuste = async (parteId) => {
    try { await api.post(`/partes/${parteId}/ajuste`); toast.success("Parte de ajuste creado"); refreshSel(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const abrirRegistro = (parteId) => { setReg({ parteId, plantilla_id: "", valores: {}, zona: "" }); setRegOpen(true); };
  const guardarRegistro = async () => {
    if (!reg.plantilla_id) return toast.error("Seleccione la plantilla");
    try {
      await api.post(`/partes/${reg.parteId}/registro-calidad`, { plantilla_id: reg.plantilla_id, valores: reg.valores, zona: reg.zona });
      toast.success("Registro de calidad enlazado al parte"); setRegOpen(false); refreshSel();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (needsScope) return <ScopeNotice />;

  const lotesMP = lotes.filter((l) => l.tipo === "mp");
  const plantillaSel = plantillas.find((p) => p.id === reg.plantilla_id);

  return (
    <div data-testid="produccion-page">
      <PageHeader overline="Nivel 3 · Traza" title="Producción"
        action={!sel && canEdit && (
          <Button onClick={() => setOrdOpen(true)} data-testid="nueva-orden-btn" className="bg-[#FF5625] hover:bg-[#E0451A]"><Plus size={16} className="mr-1" />Orden</Button>
        )} />

      {!sel ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ordenes.length === 0 && <div className="text-[#0E2A47]/40 py-12 col-span-full text-center">Aún no hay órdenes de producción.</div>}
          {ordenes.map((o) => {
            const meta = ORDEN_META[o.estado];
            return (
              <Card key={o.id} className="p-4 border-[#E2E8F0] cursor-pointer hover:border-[#FF5625]/50 transition-colors" data-testid={`orden-${o.id}`} onClick={() => openOrden(o.id)}>
                <div className="flex items-start justify-between">
                  <div className="font-mono font-black text-lg text-[#0E2A47]">{o.codigo}</div>
                  <Badge className={`text-[10px] text-white ${meta.cls}`}>{meta.label}</Badge>
                </div>
                <div className="text-sm text-[#0E2A47]/70 mt-1">{o.producto_nombre}</div>
                <div className="text-xs text-[#0E2A47]/50 mt-2">Planificado: {o.cantidad_planificada} {o.unidad}</div>
              </Card>
            );
          })}
        </div>
      ) : (
        <OrdenDetalle orden={sel} onBack={() => setSel(null)} canEdit={canEdit} lotesMP={lotesMP} productos={productos}
          onTransicion={transicion} onCrearParte={crearParte} onPrevia={verPrevia} onCerrar={cerrarParte}
          onAjuste={crearAjuste} onRegistro={abrirRegistro} onSaved={refreshSel} />
      )}

      {/* Crear orden */}
      <Dialog open={ordOpen} onOpenChange={setOrdOpen}>
        <DialogContent className="bg-white" data-testid="orden-dialog">
          <DialogHeader><DialogTitle className="text-[#0E2A47]">Nueva orden de producción</DialogTitle>
            <DialogDescription className="text-[#0E2A47]/60 text-sm">Selecciona el producto y la cantidad planificada para crear una orden.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <Fld label="Producto">
              <QuickMaestroSelect tipo="productos" value={ord.producto_id}
                onChange={(v) => setOrd({ ...ord, producto_id: v })} items={productos}
                onCreated={(item) => setProductos((prev) => [...prev, item])}
                testid="ord-producto" placeholder="Seleccione…" />
            </Fld>
            <div className="grid grid-cols-2 gap-4">
              <Fld label="Cantidad planificada"><Input type="number" min="0" step="any" value={ord.cantidad_planificada} onChange={(e) => setOrd({ ...ord, cantidad_planificada: e.target.value })} data-testid="ord-cantidad" className="mt-1.5" /></Fld>
              <Fld label="Unidad">
                <Select value={ord.unidad} onValueChange={(v) => setOrd({ ...ord, unidad: v })}>
                  <SelectTrigger data-testid="ord-unidad" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="kg">kg</SelectItem><SelectItem value="g">g</SelectItem><SelectItem value="l">l</SelectItem><SelectItem value="ud">ud</SelectItem></SelectContent>
                </Select>
              </Fld>
            </div>
          </div>
          <DialogFooter><Button onClick={crearOrden} data-testid="ord-submit" className="bg-[#0E2A47] hover:bg-[#163C65]">Crear</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Previa de balance */}
      <Dialog open={!!previa} onOpenChange={(o) => !o && setPrevia(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="previa-dialog">
          <DialogHeader><DialogTitle className="text-[#0E2A47]">Previa de balance del cierre</DialogTitle></DialogHeader>
          {previa && (
            <div className="space-y-3">
              <div className="space-y-1">
                {previa.data.lineas.map((l) => (
                  <div key={l.lote_id} className="flex items-center justify-between text-sm border-b border-[#E2E8F0] py-1.5" data-testid={`previa-linea-${l.lote_codigo}`}>
                    <span className="font-mono">{l.lote_codigo}</span>
                    <span className="text-[#0E2A47]/70">solicita {fmtNum(l.solicitado)} / disp. {fmtNum(l.disponible)}</span>
                    {l.ok ? <CheckCircle2 size={16} className="text-[#10B981]" /> : <span className="text-[#EF4444] text-xs flex items-center gap-1"><AlertTriangle size={13} />{l.motivo}</span>}
                  </div>
                ))}
              </div>
              <div className="text-sm text-[#0E2A47]"><b>PT a generar:</b> {fmtNum(previa.data.total_pt)} {previa.data.productos_generados[0]?.unidad || ""}</div>
              <div data-testid="previa-valido" data-valido={previa.data.valido} className={previa.data.valido ? "text-[#10B981] text-sm font-semibold" : "text-[#EF4444] text-sm font-semibold"}>
                {previa.data.valido ? "✓ Stock suficiente, listo para cerrar" : "✗ Hay errores, corrija antes de cerrar"}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button disabled={!previa?.data.valido} onClick={() => cerrarParte(previa.parteId)} data-testid="previa-cerrar" className="bg-[#FF5625] hover:bg-[#E0451A]"><Lock size={14} className="mr-1" />Confirmar cierre</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Registro de calidad */}
      <Dialog open={regOpen} onOpenChange={setRegOpen}>
        <DialogContent className="bg-white" data-testid="registro-dialog">
          <DialogHeader><DialogTitle className="text-[#0E2A47]">Registro de calidad (Nivel 2)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Fld label="Plantilla">
              <Select value={reg.plantilla_id} onValueChange={(v) => setReg({ ...reg, plantilla_id: v, valores: {} })}>
                <SelectTrigger data-testid="reg-plantilla" className="mt-1.5"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
                <SelectContent className="bg-white">{plantillas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </Fld>
            {plantillaSel?.campos.map((c) => (
              <Fld key={c.nombre} label={`${c.nombre}${c.unidad ? ` (${c.unidad})` : ""}${c.limite_min != null || c.limite_max != null ? ` · límites ${c.limite_min ?? "-"}…${c.limite_max ?? "-"}` : ""}`}>
                <Input type={c.tipo === "numero" ? "number" : "text"} value={reg.valores[c.nombre] ?? ""} onChange={(e) => setReg({ ...reg, valores: { ...reg.valores, [c.nombre]: e.target.value } })} data-testid={`reg-campo-${c.nombre}`} className="mt-1.5" />
              </Fld>
            ))}
            <Fld label="Zona"><Input value={reg.zona} onChange={(e) => setReg({ ...reg, zona: e.target.value })} data-testid="reg-zona" className="mt-1.5" /></Fld>
          </div>
          <DialogFooter><Button onClick={guardarRegistro} data-testid="reg-submit" className="bg-[#0E2A47] hover:bg-[#163C65]">Registrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OrdenDetalle({ orden, onBack, canEdit, lotesMP, productos, onTransicion, onCrearParte, onPrevia, onCerrar, onAjuste, onRegistro, onSaved }) {
  const meta = ORDEN_META[orden.estado];
  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-[#0E2A47]/60 hover:text-[#FF5625] mb-4" data-testid="volver-ordenes"><ArrowLeft size={15} />Volver a órdenes</button>
      <Card className="p-5 border-[#E2E8F0] mb-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-mono font-black text-2xl text-[#0E2A47]">{orden.codigo}</div>
            <div className="text-sm text-[#0E2A47]/70 mt-1">{orden.producto_nombre} · {orden.cantidad_planificada} {orden.unidad}</div>
          </div>
          <Badge className={`text-white ${meta.cls}`} data-testid="orden-estado">{meta.label}</Badge>
        </div>
        {canEdit && (
          <div className="flex gap-2 mt-4">
            {orden.estado === "planificada" && <Button size="sm" onClick={() => onTransicion("en_curso")} data-testid="orden-iniciar" className="bg-[#3B82F6] hover:bg-[#2563EB]"><Play size={14} className="mr-1" />Iniciar producción</Button>}
            {orden.estado === "en_curso" && <>
              <Button size="sm" onClick={onCrearParte} data-testid="orden-nuevo-parte" className="bg-[#FF5625] hover:bg-[#E0451A]"><Plus size={14} className="mr-1" />Nuevo parte</Button>
              <Button size="sm" variant="outline" onClick={() => onTransicion("cerrada")} data-testid="orden-cerrar"><Lock size={14} className="mr-1" />Cerrar orden</Button>
            </>}
          </div>
        )}
      </Card>

      <h2 className="text-lg font-bold text-[#0E2A47] mb-3">Partes ({orden.partes.length})</h2>
      <div className="space-y-4">
        {orden.partes.length === 0 && <div className="text-[#0E2A47]/40">Sin partes todavía.</div>}
        {orden.partes.map((p) => (
          <ParteCard key={p.id} parte={p} canEdit={canEdit} lotesMP={lotesMP} productos={productos}
            onPrevia={onPrevia} onCerrar={onCerrar} onAjuste={onAjuste} onRegistro={onRegistro} onSaved={onSaved} />
        ))}
      </div>
    </div>
  );
}

function ParteCard({ parte, canEdit, lotesMP, productos, onPrevia, onCerrar, onAjuste, onRegistro, onSaved }) {
  const abierto = parte.estado === "abierto";
  const [consumos, setConsumos] = useState(parte.consumos || []);
  const [mermas, setMermas] = useState(parte.mermas || []);
  const [pts, setPts] = useState(parte.productos_generados || []);

  useEffect(() => { setConsumos(parte.consumos || []); setMermas(parte.mermas || []); setPts(parte.productos_generados || []); }, [parte]);

  const guardar = async () => {
    const payload = {
      consumos: consumos.filter((c) => c.lote_id && c.cantidad).map((c) => ({ lote_id: c.lote_id, cantidad: Number(c.cantidad) })),
      mermas: mermas.filter((m) => m.lote_id && m.cantidad).map((m) => ({ lote_id: m.lote_id, cantidad: Number(m.cantidad), motivo: m.motivo || "" })),
      productos_generados: pts.filter((p) => p.producto_id && p.cantidad).map((p) => ({ producto_id: p.producto_id, cantidad: Number(p.cantidad), unidad: p.unidad })),
    };
    try { await api.patch(`/partes/${parte.id}`, payload); toast.success("Borrador guardado"); onSaved(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <Card className={`p-4 border-l-4 ${abierto ? "border-l-[#3B82F6]" : "border-l-[#0E2A47]"} border-[#E2E8F0]`} data-testid={`parte-${parte.id}`} data-parte-inmutable={parte.inmutable}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="font-mono font-bold text-[#0E2A47]">{parte.codigo}</span>
          {parte.tipo === "ajuste" && <Badge variant="outline" className="text-[10px] border-[#F59E0B] text-[#B45309]"><GitFork size={10} className="mr-1" />Ajuste de {parte.parte_original_codigo}</Badge>}
          <Badge className={abierto ? "bg-[#3B82F6] hover:bg-[#3B82F6] text-[10px]" : "bg-[#0E2A47] hover:bg-[#0E2A47] text-[10px]"} data-testid={`parte-estado-${parte.id}`}>{abierto ? "Abierto" : "Cerrado · inmutable"}</Badge>
        </div>
        {parte.ajustes?.length > 0 && <span className="text-xs text-[#B45309]" data-testid={`parte-ajustado-${parte.id}`}>Ajustado por {parte.ajustes.join(", ")}</span>}
      </div>

      {abierto && canEdit ? (
        <div className="space-y-4">
          {/* Consumos */}
          <EditableRows title="Consumos de materia prima" rows={consumos} setRows={setConsumos} testid="consumo"
            newRow={{ lote_id: "", cantidad: "" }} render={(row, i, update) => (
              <>
                <Select value={row.lote_id} onValueChange={(v) => update(i, { ...row, lote_id: v })}>
                  <SelectTrigger data-testid={`consumo-lote-${i}`} className="h-9"><SelectValue placeholder="Lote MP" /></SelectTrigger>
                  <SelectContent className="bg-white">{lotesMP.map((l) => <SelectItem key={l.id} value={l.id} disabled={l.estado !== "disponible"}>{l.codigo} · {l.materia_prima_nombre} (disp. {l.disponible}{l.estado !== "disponible" ? `, ${l.estado}` : ""})</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" min="0" step="any" placeholder="Cantidad" value={row.cantidad} onChange={(e) => update(i, { ...row, cantidad: e.target.value })} data-testid={`consumo-cantidad-${i}`} className="h-9 w-32" />
              </>
            )} />
          {/* Mermas */}
          <EditableRows title="Mermas (motivo obligatorio)" rows={mermas} setRows={setMermas} testid="merma"
            newRow={{ lote_id: "", cantidad: "", motivo: "" }} render={(row, i, update) => (
              <>
                <Select value={row.lote_id} onValueChange={(v) => update(i, { ...row, lote_id: v })}>
                  <SelectTrigger data-testid={`merma-lote-${i}`} className="h-9"><SelectValue placeholder="Lote" /></SelectTrigger>
                  <SelectContent className="bg-white">{lotesMP.map((l) => <SelectItem key={l.id} value={l.id}>{l.codigo} (disp. {l.disponible})</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" min="0" step="any" placeholder="Cantidad" value={row.cantidad} onChange={(e) => update(i, { ...row, cantidad: e.target.value })} data-testid={`merma-cantidad-${i}`} className="h-9 w-24" />
                <Input placeholder="Motivo" value={row.motivo} onChange={(e) => update(i, { ...row, motivo: e.target.value })} data-testid={`merma-motivo-${i}`} className="h-9 flex-1" />
              </>
            )} />
          {/* PT */}
          <EditableRows title="Producto terminado a generar" rows={pts} setRows={setPts} testid="pt"
            newRow={{ producto_id: "", cantidad: "", unidad: "kg" }} render={(row, i, update) => (
              <>
                <Select value={row.producto_id} onValueChange={(v) => update(i, { ...row, producto_id: v })}>
                  <SelectTrigger data-testid={`pt-producto-${i}`} className="h-9"><SelectValue placeholder="Producto" /></SelectTrigger>
                  <SelectContent className="bg-white">{productos.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" min="0" step="any" placeholder="Cantidad" value={row.cantidad} onChange={(e) => update(i, { ...row, cantidad: e.target.value })} data-testid={`pt-cantidad-${i}`} className="h-9 w-32" />
              </>
            )} />

          <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E2E8F0]">
            <Button size="sm" variant="outline" onClick={guardar} data-testid={`parte-guardar-${parte.id}`}>Guardar borrador</Button>
            <Button size="sm" variant="outline" onClick={() => onRegistro(parte.id)} data-testid={`parte-registro-${parte.id}`}><FlaskConical size={14} className="mr-1" />Registro calidad</Button>
            <Button size="sm" onClick={() => onPrevia(parte.id)} data-testid={`parte-previa-${parte.id}`} className="bg-[#FF5625] hover:bg-[#E0451A]"><ClipboardCheck size={14} className="mr-1" />Previa y cerrar</Button>
          </div>
          {parte.registros_calidad_ids?.length > 0 && <div className="text-xs text-[#10B981]">✓ {parte.registros_calidad_ids.length} registro(s) de calidad enlazado(s)</div>}
        </div>
      ) : (
        <ParteResumen parte={parte} canEdit={canEdit} onAjuste={onAjuste} />
      )}
    </Card>
  );
}

function ParteResumen({ parte, canEdit, onAjuste }) {
  return (
    <div className="space-y-3 text-sm">
      {parte.consumos?.length > 0 && <div><b className="text-[#0E2A47]">Consumos:</b> {parte.consumos.map((c) => `${c.lote_codigo} (${c.cantidad})`).join(", ")}</div>}
      {parte.mermas?.length > 0 && <div><b className="text-[#0E2A47]">Mermas:</b> {parte.mermas.map((m) => `${m.lote_codigo} ${m.cantidad} — ${m.motivo}`).join("; ")}</div>}
      {parte.productos_generados?.length > 0 && parte.productos_generados[0].lote_codigo && (
        <div><b className="text-[#0E2A47]">Lote PT generado:</b> {parte.productos_generados.map((p) => `${p.lote_codigo} · ${p.cantidad} ${p.unidad}`).join(", ")}</div>
      )}
      {parte.composicion?.length > 0 && (
        <div className="bg-[#F7F8FA] rounded-md p-3">
          <div className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60 mb-1">Composición (trazabilidad)</div>
          {parte.composicion.map((c, i) => <div key={i} className="text-xs text-[#0E2A47]/80">← {c.lote_codigo} · {c.materia_prima_nombre} · {c.cantidad}</div>)}
        </div>
      )}
      {parte.registros_calidad_ids?.length > 0 && <div className="text-xs text-[#10B981]">✓ {parte.registros_calidad_ids.length} registro(s) de calidad enlazado(s)</div>}
      {canEdit && parte.tipo !== "ajuste" && (
        <Button size="sm" variant="outline" onClick={() => onAjuste(parte.id)} data-testid={`parte-ajuste-${parte.id}`}><GitFork size={14} className="mr-1" />Crear parte de ajuste</Button>
      )}
    </div>
  );
}

function EditableRows({ title, rows, setRows, render, newRow, testid }) {
  const update = (i, row) => setRows(rows.map((r, idx) => (idx === i ? row : r)));
  const remove = (i) => setRows(rows.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60 mb-1.5">{title}</div>
      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={i} className="flex flex-wrap sm:flex-nowrap items-center gap-2" data-testid={`${testid}-row-${i}`}>
            {render(row, i, update)}
            <Button size="icon" variant="ghost" className="h-9 w-9 text-[#EF4444]" onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`}><Trash2 size={14} /></Button>
          </div>
        ))}
        <Button size="sm" variant="ghost" className="text-[#3B82F6] h-8" onClick={() => setRows([...rows, { ...newRow }])} data-testid={`add-${testid}`}><Plus size={14} className="mr-1" />Añadir</Button>
      </div>
    </div>
  );
}

function Fld({ label, children }) {
  return (<div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">{label}</Label>{children}</div>);
}
